import {FC, useEffect, useReducer, useState} from 'react'
import Logo from 'assets/images/DS-logo-white@2x.png'
import {useQueryClient} from '@tanstack/react-query'
import {useSelector} from 'react-redux'
import {Link} from 'react-router-dom'
import styled from 'styled-components'
import classNames from 'classnames'

import {dispatch} from 'app/store'
import {
  QuestionIcon,
  ArrowRightSmIcon,
  LoadingSpinner,
  WindowsIcon,
  AppleIcon,
  ReleaseNotesIcon,
  ArrowUpRightIcon,
  RenderIf,
  BellIcon,
} from 'components'
import {usePolicy, useNativeInfo} from 'hooks'
import {NavLink} from './TopNavLink'
import {
  selectUser,
  setImportStatusTimerRunning,
  userActions,
} from 'features/User'
import {selectedEndpointDebugMode} from 'features/Endpoints/endpointsSelectors'
import {
  GetImportStatusResponse,
  EndpointRoutesEnum,
  SliceStatus,
  SSLType,
  PolicyRoutesEnum,
  CASBRoutesEnum,
  AccountList,
} from 'interfaces'
import {ArrowIcon, DownloadIcon} from 'components/Basic'
import {
  basePolicyRoutes,
  colorCodes,
  downloadLinks,
  num0,
  secs10,
  settingsUsersRoute,
} from 'definitions/constant'
import {settingsActions} from 'features/Settings'
import HelpSection from './components/HelpSection'
import Toast from 'components/Basic/Toast'
import {history, isJWTTokenExpired, prefetchTranslations} from 'utils'
import {requestHandler, SettingsService} from 'services/api'
import {selectImportStatusState} from 'features/Settings/Users/userSelector'
import NotificationSection from './components/NotificationSection'
import {LogoutContainer} from './components/LogoutContainer'

import {fetchNotifications} from './components/NotificationSection/service'
import {
  sslErrorActions,
  sslErrorNotificationInitState,
  sslErrorNotificationReducer,
} from './components/NotificationSection/reducer'
import {SSLErrorNotificationContext} from './components/NotificationSection/context'
import {prewarmEndpoints, useContextualPrefetch} from 'prefetch'
import {prewarmPolicies} from 'prefetch/prewarmPolicies'
import {CanIAccess, PERMISSIONS, withPermission} from 'rbac'
import {useSettingsDefaultPage} from 'hooks'
import {EndPoints, HttpMethods} from 'services/api/HTTP/HTTP.types'

enum TopNavigationTab {
  none,
  nameTab,
  download,
  help,
  notification,
}

const NavContainer = styled.div`
  width: 100%;
  height: 3.5rem;
`
const importStatusTimerInterval = 10000
let importStatusInterval: ReturnType<typeof setInterval>
let notificationInterval: ReturnType<typeof setInterval>

interface TopNavigationProps {
  isForFallback?: boolean
}

const downloadInstallerData = [
  {
    label: 'Windows Download',
    key: 'windows',
  },
  {
    label: 'Apple Download',
    key: 'mac',
  },
]

const avoidRoutes = [
  '/preview',
  '/settings/grant-access',
  CASBRoutesEnum.GRANT_ACCESS,
  CASBRoutesEnum.ACCESS_GRANTED,
  CASBRoutesEnum.ACCESS_DENIED,
  CASBRoutesEnum.SYNC_FAILURE,
  '/settings',
  '/tenants',
  '/callback',
]

export const TopNavigation: FC<TopNavigationProps> = ({
  isForFallback = false,
}) => {
  const {pathname: currentRoute} = useNativeInfo()
  const {
    onboardingCompleted,
    name,
    emailId,
    downloadInstallerStatus,
    multiAccount,
    usersImported,
    isImportStatusTimerRunning,
  } = useSelector(selectUser)
  const {
    data: {reauthRequired},
  } = useSelector(selectImportStatusState)

  const settingsDefaultPage = useSettingsDefaultPage()

  const queryClient = useQueryClient()

  const canReadNotifications = withPermission([PERMISSIONS.NOTIFICATIONS.READ])
  const canReadImportStatus = withPermission([PERMISSIONS.USER_MANAGEMENT.READ])

  const {prefetch} = useContextualPrefetch()

  const [currentRightTab, setCurrentRightTab] = useState<TopNavigationTab>(
    TopNavigationTab.none,
  )
  const [selectedBundle, setSelectedBundle] = useState('')
  const [selectedTenant, setSelectedTenant] = useState('')
  const [sslErrorNotificationState, sslErrorActionDispatcher] = useReducer(
    sslErrorNotificationReducer,
    sslErrorNotificationInitState,
  )

  // creating separate local state for user import status
  // this state is being used to show re-auth required warning toast
  // this will help to common redux state overrides
  const [importStatusState, setImportStatusState] =
    useState<GetImportStatusResponse>({} as GetImportStatusResponse)
  const {isBasePolicyToBeColored} = usePolicy()
  const pathname = history.location.pathname

  const isDebugMode = useSelector(selectedEndpointDebugMode)

  const onLogout = async (e: React.MouseEvent<HTMLElement>): Promise<void> => {
    e.stopPropagation()
    // If timer is running, stop it
    importStatusInterval && clearInterval(importStatusInterval)
    notificationInterval && clearInterval(notificationInterval)
    isImportStatusTimerRunning && dispatch(setImportStatusTimerRunning(false))
    setCurrentRightTab(TopNavigationTab.none)
    dispatch(userActions.logout())
  }

  useEffect(() => {
    const controller = new AbortController()
    const {signal: globalClickHandlerSignal} = controller

    const handleClick = (e: MouseEvent) => {
      const ids = ['logout', 'name', 'email', 'name icon', 'download-icon']
      const notificationEl = document.getElementById('notification-section')

      if (!ids.includes((e.target as HTMLElement)?.id ?? '')) {
        // safe way to close notification container without any regression
        if (notificationEl?.contains(e.target as Node)) {
          return
        }
        setCurrentRightTab(TopNavigationTab.none)
      }
    }

    window.addEventListener('click', handleClick, {
      signal: globalClickHandlerSignal,
    })
    return () => {
      controller.abort()
    }
  }, [])

  const onFileDownload = (
    e: React.MouseEvent<HTMLElement>,
    bundle: string,
  ): void => {
    e.stopPropagation()
    setSelectedBundle(bundle)
    dispatch(userActions.downloadInstaller({bundle}))
  }

  const onProfileClick = (e: React.MouseEvent<HTMLElement>): void => {
    e.stopPropagation()
    setCurrentRightTab(TopNavigationTab.nameTab)
  }

  const onAccountSwitch = async (
    e: React.MouseEvent<HTMLElement>,
    value: string,
  ): Promise<void> => {
    let token = multiAccount?.details?.token
    setSelectedTenant(value)
    e.stopPropagation()
    // Check if switch account token is expired or not
    if (isJWTTokenExpired(token)) {
      try {
        // If token is expired, refresh the token
        const {data} = await requestHandler<AccountList>({
          method: HttpMethods.GET,
          url: EndPoints.SwitchAccount,
        })
        token = data.token
      } catch (_error) {
        // Logout user if refresh switch account token fails
        dispatch(userActions.setTokensFailure())
      }
    }
    dispatch(
      userActions.setTokens({
        token,
        tenants: btoa(value),
        pageRefresh: true,
      }),
    )
  }

  const getSSLErrorDetails = async () => {
    sslErrorActionDispatcher(sslErrorActions.getSSLErrorNotifications())
    const result = await fetchNotifications({
      offset: num0,
      type: SSLType.app,
    })
    sslErrorActionDispatcher(
      sslErrorActions.getSSLErrorNotificationsSuccess(result),
    )
  }

  const startNotificationPolling = () => {
    if (!notificationInterval) {
      notificationInterval = setInterval(() => {
        getSSLErrorDetails()
      }, secs10)
    }
  }

  useEffect(() => {
    // prefetch common translation file on mount.
    // this change avoids page blink issue on SSL notification panel.
    prefetchTranslations(['common'])

    if (!isForFallback) dispatch(userActions.getAccountsList())
    return () => {
      setCurrentRightTab(TopNavigationTab.none)
      importStatusInterval && clearInterval(importStatusInterval)
      notificationInterval && clearInterval(notificationInterval)
      isImportStatusTimerRunning && dispatch(setImportStatusTimerRunning(false))
    }
  }, [])

  // this logic is stop polling when notification panel is visible
  useEffect(() => {
    if (!isForFallback) {
      // selected tab is not notification then start notification polling
      // else stop polling and do normal network call
      if (
        !notificationInterval &&
        currentRightTab !== TopNavigationTab.notification &&
        canReadNotifications
      ) {
        getSSLErrorDetails()
        startNotificationPolling()
      } else {
        notificationInterval && clearInterval(notificationInterval)
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        notificationInterval = 0 as any
      }
    }
  }, [currentRightTab, canReadNotifications])

  useEffect(() => {
    if (isImportStatusTimerRunning) {
      startPollingUserImport()
    }
  }, [isImportStatusTimerRunning])

  // If usersImported values changes to true, stop the timer
  // Set isImportStatusTimerRunning to false
  useEffect(() => {
    if (usersImported && !reauthRequired) {
      importStatusInterval && clearInterval(importStatusInterval)
      isImportStatusTimerRunning && dispatch(setImportStatusTimerRunning(false))
    }
  }, [usersImported, reauthRequired])

  // If usersImported and emailId values available means user is logged in.
  // Call import status api to verify the user import sync status.
  useEffect(() => {
    if (usersImported && emailId && canReadImportStatus) {
      void initGetImportStatusCall()
    }
  }, [usersImported, emailId, canReadImportStatus])

  const initGetImportStatusCall = async () => {
    try {
      const res = await SettingsService.getImportStatus()
      setImportStatusState(res?.data)
    } catch (_) {
      // no operation
    }
  }

  const startPollingUserImport = () => {
    // If usersImported is false
    // Start polling import status api every 5 seconds
    // Else clear the interval
    if (!usersImported || reauthRequired) {
      // Start timer only if no previous timer is present
      if (!importStatusInterval) {
        importStatusInterval = setInterval(() => {
          dispatch(settingsActions.getImportStatus())
        }, importStatusTimerInterval)
      }
    } else {
      importStatusInterval && clearInterval(importStatusInterval)
      isImportStatusTimerRunning && dispatch(setImportStatusTimerRunning(false))
    }
  }

  // on policy listing screen selected policy is set to base policy
  // to avoid any sort of regression policy listing screen this local var is useful.
  const isTopBarToBeBlueColor =
    (isBasePolicyToBeColored && basePolicyRoutes.includes(currentRoute)) ||
    isDebugMode

  const iconClick = (selectedTab: TopNavigationTab) => {
    currentRightTab === selectedTab
      ? setCurrentRightTab(TopNavigationTab.none)
      : setCurrentRightTab(selectedTab)
  }

  const onNavClick = (path: string): void => {
    switch (path) {
      case EndpointRoutesEnum.BASE:
        prewarmEndpoints(queryClient, prefetch)
        break
      case PolicyRoutesEnum.BASE:
        prewarmPolicies(prefetch)
        break
      default:
        break
    }
  }

  return (
    <>
      <NavContainer
        className={classNames(
          'w-screen  flex justify-between items-center border-b-0.5 border-gray-400 relative px-6',
          {
            hidden: !onboardingCompleted || avoidRoutes.includes(currentRoute),
            'bg-dope-teal-100': isTopBarToBeBlueColor,
            'bg-dope-black': !isTopBarToBeBlueColor,
          },
        )}
        id="top-navigation"
        data-cy="top-navigation-bar"
      >
        {/* Check sync status and show warning toast message */}
        <RenderIf isTrue={!currentRoute?.includes('/settings/grant-access')}>
          <CheckUserImportStatus
            currentRoute={currentRoute}
            reAuthRequired={Boolean(importStatusState?.reauthRequired)}
          />
        </RenderIf>
        <div className="flex items-center">
          <Link to="/analytics/overview" className="mr-7">
            <img src={Logo} alt="logo" className="object-contain w-8 h-8" />
          </Link>
          <NavLink
            pathname="/analytics/overview"
            current={pathname.includes('analytics')}
            linkName="Analytics"
          />
          <NavLink
            pathname="/policies"
            onClick={() => onNavClick(PolicyRoutesEnum.BASE)}
            current={pathname.includes('policies')}
            linkName="SWG"
          />
          <NavLink
            pathname={CASBRoutesEnum.BASE}
            current={pathname.includes('casb')}
            linkName="CASB"
          />
          <NavLink
            pathname="/endpoints"
            current={pathname === '/endpoints'}
            linkName="Endpoints"
            onClick={() => onNavClick(EndpointRoutesEnum.BASE)}
          />
          <NavLink
            pathname={settingsDefaultPage}
            current={pathname.includes('settings')}
            linkName="Settings"
          />
        </div>

        <ul className="flex items-center text-white abc-regular h-full">
          <CanIAccess reqPermissions={[PERMISSIONS.NOTIFICATIONS.READ]}>
            <li
              className={classNames('mr-4 h-full cursor-pointer', {
                'border-b-3 border-white':
                  currentRightTab === TopNavigationTab.notification,
              })}
            >
              <button
                data-cy="notification-bell-icon"
                aria-label="Click Notification Bell Icon to view SSL Pinning Errors"
                className="h-14 relative"
                onClick={e => {
                  e.stopPropagation()
                  iconClick(TopNavigationTab.notification)
                }}
              >
                <BellIcon classes="hover:text-white" />
                <RenderIf
                  isTrue={
                    sslErrorNotificationState?.totalAppCount > 0 ||
                    sslErrorNotificationState?.totalUrlCount > 0
                  }
                >
                  <span
                    data-cy="pending-notification-indicator"
                    className={classNames(
                      'h-2.5 w-2.5 flex justify-center items-center absolute right-0.5 top-3.5',
                      {
                        'bg-dope-teal-100': isTopBarToBeBlueColor,
                        'bg-dope-black': !isTopBarToBeBlueColor,
                      },
                    )}
                  >
                    <span className="relative h-1.5 w-1.5 flex">
                      <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-dope-purple-50 opacity-75"></span>
                      <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-dope-purple-50"></span>
                    </span>
                  </span>
                </RenderIf>
              </button>
            </li>
          </CanIAccess>
          <li
            className={classNames('mr-4 h-full cursor-pointer', {
              'border-b-3 border-white':
                currentRightTab === TopNavigationTab.download,
            })}
          >
            <button
              id="download-icon"
              className="h-14"
              onClick={() => iconClick(TopNavigationTab.download)}
            >
              <DownloadIcon
                strokeColor="text-gray-1000"
                strokeWidth={1}
                id="download-icon"
                width={24}
                height={24}
                classes={'hover:text-white'}
              />
            </button>
          </li>
          <li
            className={classNames('mr-4 h-full cursor-pointer', {
              'border-b-3 border-white':
                currentRightTab === TopNavigationTab.help,
            })}
          >
            <button
              id="question-icon"
              className="h-14"
              onClick={e => {
                e.stopPropagation()
                iconClick(TopNavigationTab.help)
              }}
            >
              <QuestionIcon
                strokeColor="text-gray-1000"
                width={24}
                height={24}
                classes={'hover:text-white'}
              />
            </button>
          </li>
          <li
            className={classNames('cursor-pointer h-14 pt-4', {
              'border-b-3 border-white':
                currentRightTab === TopNavigationTab.nameTab,
            })}
          >
            <span
              id="name icon"
              data-cy="profile-icon"
              onClick={e =>
                currentRightTab === TopNavigationTab.nameTab
                  ? setCurrentRightTab(TopNavigationTab.none)
                  : onProfileClick(e)
              }
              className="rounded-full w-6 h-6 bg-dope-cyan-200 hover:bg-dope-cyan-100 flex justify-center items-center uppercase"
            >
              {name?.charAt(0) || '?'}
            </span>
          </li>
        </ul>
      </NavContainer>
      {(currentRightTab === TopNavigationTab.nameTab ||
        currentRightTab === TopNavigationTab.download ||
        currentRightTab === TopNavigationTab.help ||
        currentRightTab === TopNavigationTab.notification) && (
        <div
          data-cy="top-navigation-dropdown"
          className={classNames(
            'h-full z-40 fixed w-full top-14 bg-dope-black bg-opacity-0',
            {
              hidden:
                !onboardingCompleted || avoidRoutes.includes(currentRoute),
            },
          )}
        >
          <div className="grid-cols-14">
            <div className="col-start-3 grid grid-cols-14 col-span-14">
              <RenderIf
                isTrue={currentRightTab === TopNavigationTab.notification}
              >
                <SSLErrorNotificationContext.Provider
                  value={[sslErrorNotificationState, sslErrorActionDispatcher]}
                >
                  <NotificationSection
                    isTopBarToBeBlueColor={isTopBarToBeBlueColor}
                  />
                </SSLErrorNotificationContext.Provider>
              </RenderIf>
              <RenderIf isTrue={currentRightTab === TopNavigationTab.help}>
                <HelpSection isTopBarToBeBlueColor={isTopBarToBeBlueColor} />
              </RenderIf>
              <RenderIf isTrue={currentRightTab === TopNavigationTab.nameTab}>
                <LogoutContainer
                  name={name}
                  pathname={pathname}
                  emailId={emailId}
                  selectedTenant={selectedTenant}
                  onLogout={onLogout}
                  onAccountSwitch={onAccountSwitch}
                  isTopBarToBeBlueColor={isTopBarToBeBlueColor}
                />
              </RenderIf>
              <RenderIf isTrue={currentRightTab === TopNavigationTab.download}>
                <div
                  id="installer-download"
                  className={classNames(
                    'pt-4 pb-1 border-l-0.5 border-b-0.5 border-r-0.5 border-gray-400 z-50 absolute right-24 w-56',
                    {
                      'bg-dope-black': !isTopBarToBeBlueColor,
                      'bg-dope-teal-100': isTopBarToBeBlueColor,
                    },
                  )}
                >
                  <p className="text-sm karla font-normal pb-2 px-6 border-b-0.5 border-gray-1200">
                    Endpoint Client
                  </p>
                  {downloadInstallerData.map(
                    (ele: {label: string; key: string}) => {
                      const uniqueKey = `download-${ele.key}`
                      return (
                        <div
                          className={classNames(
                            'py-4 px-6 flex flex-row justify-between items-center cursor-pointer h-8 border-b-1 border-gray-1200',
                            {
                              'hover:bg-gray-800': !isTopBarToBeBlueColor,
                              'hover:bg-dope-teal-300': isTopBarToBeBlueColor,
                            },
                          )}
                          key={uniqueKey}
                          data-cy={uniqueKey}
                          onClick={e => onFileDownload(e, ele.key)}
                        >
                          <div className="flex flex-row items-center gap-2">
                            <RenderIf isTrue={ele.key === 'windows'}>
                              <WindowsIcon />
                            </RenderIf>
                            <RenderIf isTrue={ele.key !== 'windows'}>
                              <AppleIcon />
                            </RenderIf>
                            <p className="karla font-normal text-xs w-32 text-white underline decoration-0">
                              {ele.label}
                            </p>
                          </div>
                          <RenderIf
                            isTrue={
                              downloadInstallerStatus === SliceStatus.pending &&
                              selectedBundle === ele.key
                            }
                          >
                            <span
                              className="mr-2"
                              data-cy={`download-${ele.key}-spinner`}
                              onClick={e => e.stopPropagation()}
                            >
                              <LoadingSpinner
                                type="TailSpin"
                                color={colorCodes.white}
                                height={12}
                                width={12}
                              />
                            </span>
                          </RenderIf>
                          <RenderIf
                            isTrue={
                              !(
                                downloadInstallerStatus ===
                                  SliceStatus.pending &&
                                selectedBundle === ele.key
                              )
                            }
                          >
                            <ArrowRightSmIcon classes="cursor-pointer" />
                          </RenderIf>
                        </div>
                      )
                    },
                  )}
                  <div
                    className={classNames(
                      'py-4 pl-5 pr-6 flex justify-between items-center h-8 cursor-pointer',
                      {
                        'hover:bg-gray-800': !isTopBarToBeBlueColor,
                        'hover:bg-dope-teal-300': isTopBarToBeBlueColor,
                      },
                    )}
                  >
                    <div className="flex flex-row items-center gap-1">
                      <ReleaseNotesIcon />
                      <p
                        className="karla text-xs w-32 text-white underline decoration-0"
                        onClick={e => {
                          e.preventDefault()
                          window.open(downloadLinks.releaseNotes, '_blank')
                        }}
                      >
                        Release Notes
                      </p>
                    </div>

                    <ArrowUpRightIcon classes="cursor-pointer" />
                  </div>
                </div>
              </RenderIf>
            </div>
          </div>
        </div>
      )}
    </>
  )
}

interface CheckUserImportStatusProps {
  reAuthRequired: boolean
  currentRoute: string
}
const CheckUserImportStatus: FC<CheckUserImportStatusProps> = ({
  reAuthRequired,
  currentRoute,
}) => {
  const [showToastOnce, setShowToastOnce] = useState<boolean>(true)

  const canIManageUsers = withPermission([PERMISSIONS.USER_MANAGEMENT.WRITE])

  useEffect(() => {
    if (reAuthRequired && showToastOnce && canIManageUsers) {
      const isSettingsUsersPage = currentRoute === settingsUsersRoute
      const reAuthMessage = () => (
        <p>
          User import is no longer synced. Please reconnect to reinstate
          auto-sync of users.{' '}
          <RenderIf isTrue={!isSettingsUsersPage}>
            <span
              onClick={() => {
                history.push(settingsUsersRoute)
              }}
              className="text-yellow-100 cursor-pointer hover:underline decoration-0"
            >
              Take me there{' '}
              <ArrowIcon
                height={12}
                width={24}
                classes="text-yellow-100 inline-block"
                strokeWidth={0.1}
              />
            </span>
          </RenderIf>
        </p>
      )
      Toast({
        message: reAuthMessage,
        type: 'warning',
        style: {width: isSettingsUsersPage ? '700px' : '835px'},
      })
      setShowToastOnce(false)
    }
  }, [reAuthRequired, showToastOnce])

  return <></>
}
